import { useEffect, useRef } from "react";
import { useGeolocationStore } from "@/shared/geo/geolocationStore";
import { useSessionStore } from "@/shared/stores/sessionStore";

/**
 * Sollicite la localisation À CHAQUE CONNEXION — sans rien bloquer.
 *
 * Monté dans les DEUX espaces (client et agent) : la règle est la même pour
 * tout le monde.
 *
 * ## Le défaut que ce composant corrigeait mal
 * La sollicitation était gardée derrière `hasEverGranted`, un drapeau
 * `localStorage` (`alomain.geo.granted`) posé au premier accord et JAMAIS
 * effacé — ni au logout, ni au changement de compte. Il est donc à l'échelle du
 * NAVIGATEUR, pas de l'utilisateur.
 *
 * Conséquence observée : un client autorise la position, se déconnecte, puis
 * quelqu'un se connecte en AGENT sur le même navigateur. Le drapeau est encore
 * là, la garde se referme, et l'agent n'est jamais sollicité — alors qu'il est
 * précisément celui dont le métier en dépend. Pire, si la permission a été
 * révoquée entre-temps (réglages du site, ou navigateur qui ne la retient pas),
 * l'app se croit autorisée et n'obtient jamais rien.
 *
 * Second trou : la sollicitation n'avait lieu que sur `permission === "prompt"`.
 * Un utilisateur en `denied` ne recevait donc RIEN — ni invite, ni explication.
 * Sa localisation était coupée sans qu'on le lui dise nulle part.
 *
 * ## La règle appliquée maintenant
 * On regarde l'état RÉEL du navigateur, à chaque connexion, et pour chaque
 * utilisateur :
 *
 * - **`granted`** → rien. La localisation est active, on ne redemande rien.
 * - **`prompt`** → invite native. Refusée ou ignorée, le modal explicatif prend
 *   le relais.
 * - **`denied` / `unsupported`** → l'invite native est morte (le refus est
 *   mémorisé par origine, `getCurrentPosition` échouerait sans rien afficher) :
 *   le modal s'ouvre directement, avec la marche à suivre.
 *
 * ## Ce que ça ne fait toujours pas : bloquer
 * Un refus laisse l'app entièrement utilisable — consulter ses commandes, son
 * portefeuille, son profil n'exige aucune position. La relance a lieu à la
 * connexion suivante, et à chaque ACTION qui en dépend réellement, via
 * `requirePosition`.
 *
 * Ne rend aucun élément : ce n'est qu'un point de départ.
 */
export function LocationGate() {
  const permission = useGeolocationStore((s) => s.permission);
  const init = useGeolocationStore((s) => s.init);
  const request = useGeolocationStore((s) => s.request);
  const openPermissionDialog = useGeolocationStore((s) => s.openPermissionDialog);

  // L'identité EST la clé de réarmement : c'est ce qui fait qu'une nouvelle
  // connexion sollicite à nouveau, là où un drapeau de navigateur ne le faisait
  // jamais. Un simple rechargement de page, lui, ne redemande rien de plus que
  // ce que l'état de permission impose déjà.
  const userId = useSessionStore((s) => s.userId);

  useEffect(() => {
    init();
  }, [init]);

  // UNE sollicitation par utilisateur et par montage. Sans cette garde, Firefox
  // rouvrait une invite en boucle : il ne mémorise la permission que si
  // « Retenir ce choix » est coché, si bien que `permissions.query` repasse à
  // `prompt` juste après que l'utilisateur a accepté.
  const solicitedForRef = useRef<string | null>(null);

  useEffect(() => {
    // Pas encore connecté : il n'y a personne à solliciter.
    if (!userId) return;
    // `init()` n'a pas encore tranché — patienter évite une invite prématurée.
    if (permission === "unknown") return;
    // Déjà active : on ne demande plus rien. C'est la moitié silencieuse de la
    // règle, et la plus importante pour ne pas harceler.
    if (permission === "granted") return;
    if (solicitedForRef.current === userId) return;

    solicitedForRef.current = userId;

    if (permission === "prompt") {
      // L'invite native reste possible : c'est le chemin le plus court, et le
      // seul qui puisse accorder la permission en un geste.
      void request().then((granted) => {
        if (!granted) openPermissionDialog();
      });
      return;
    }

    // `denied` ou `unsupported` : rien à tenter, tout à expliquer.
    openPermissionDialog();
  }, [userId, permission, request, openPermissionDialog]);

  return null;
}
